/**
 * outputFieldRows (#2373) — pure row shaping/sort/Σ for the per-field detail
 * table (OutputFieldTable) under the Ernteprognose chart. Rows = the selected Kultur·Sorte's
 * current-WJ field-cycles (one row per Schlag); the chart aggregates the group
 * over time, this breaks it down per field. All shaping is pure + unit-tested
 * (recharts/jsdom-free), mirroring Deckungsbeitrag's ``deckungsbeitragRollup``.
 */
import type { OutputCycleInfo } from '../../../hooks/useErnteprognose';

/** One per-field row: identity + Ist / Prognose / Plan / Erfüllung / last pick. */
export interface FieldRow {
  cropCycleId: string;
  fieldId: string | null;
  /** Schlag (field) name — "Ohne Schlag" when unresolved. */
  schlag: string;
  areaHa: number | null;
  /** Season-to-date Σ of the cycle's picks (0 when none harvested yet). */
  istKg: number;
  /** Σ of the cycle's yield_forecast values; null → "—" (no forecast). */
  prognoseKg: number | null;
  /** Latest yield_plan revision value; null → "—" (no plan, #2344 pending). */
  planKg: number | null;
  /** Ist / Prognose as a fraction; null when there is no forecast denominator. */
  erfuellung: number | null;
  /** Max pick date (letzter Pflücktag); null when nothing picked. */
  letzterPfluecktag: string | null;
}

const round1 = (n: number): number => Math.round(n * 10) / 10;

/** Latest-revision CURRENT-WJ plan value for one cycle (mirrors ErnteprognosePage.pickPlanTarget):
 *  plans whose ``planPeriod`` is null or ≠ ``currentSeason`` are dropped BEFORE the latest-revision
 *  reduce, so the table Plan column matches the chart Plan line + the lens (#2433 / #2419). */
function latestPlan(
  plans: { cropCycleId: string | null; value: number | null; planPeriod: string | null; asPlannedAt: string | null }[],
  cropCycleId: string,
  currentSeason: number,
): number | null {
  const withValue = plans.filter(
    (p) => p.cropCycleId === cropCycleId && p.value != null && p.planPeriod != null && Number(p.planPeriod) === currentSeason,
  );
  if (withValue.length === 0) return null;
  const latest = withValue.reduce((a, b) => ((b.asPlannedAt ?? '') > (a.asPlannedAt ?? '') ? b : a));
  return latest.value as number;
}

/**
 * Shape one row per current-season field-cycle. Ist = Σ picks; Prognose = Σ
 * forecast (null when the cycle has none); Plan = latest revision (null → "—");
 * Erfüllung = Ist / Prognose (null denominator ⇒ null); letzter Pflücktag = max
 * pick date.
 *
 * ``picks`` arrive ALREADY season-sliced (the page's ``seasonCurrentPicks`` seam,
 * #2413) and are summed whole. There is deliberately no date window here: the
 * optional ``window`` parameter this function used to take existed only to
 * re-apply the chart's Wirtschaftsjahr clamp, and #2601/#2610 removed that clamp
 * — the season, not the calendar, decides what belongs to the current Ist.
 *
 * ``currentSeason`` gates the Plan column: only plans whose ``planPeriod === currentSeason``
 * feed ``planKg`` (via ``latestPlan``), so the table Plan matches the chart + lens (#2433).
 */
export function buildFieldRows(args: {
  members: OutputCycleInfo[];
  picks: { cropCycleId: string | null; date: string; qty: number }[];
  forecasts: { cropCycleId: string | null; value: number | null }[];
  plans: { cropCycleId: string | null; value: number | null; planPeriod: string | null; asPlannedAt: string | null }[];
  fieldNames: Record<string, string>;
  currentSeason: number;
}): FieldRow[] {
  const { members, picks, forecasts, plans, fieldNames, currentSeason } = args;
  return members.map((m): FieldRow => {
    const myPicks = picks.filter((p) => p.cropCycleId === m.cropCycleId);
    const istKg = round1(myPicks.reduce((acc, p) => acc + p.qty, 0));
    const letzterPfluecktag = myPicks.reduce<string | null>(
      (max, p) => (max == null || p.date > max ? p.date : max),
      null,
    );

    const myForecasts = forecasts.filter((f) => f.cropCycleId === m.cropCycleId && f.value != null);
    const prognoseKg = myForecasts.length > 0 ? round1(myForecasts.reduce((acc, f) => acc + (f.value as number), 0)) : null;

    const planKg = latestPlan(plans, m.cropCycleId, currentSeason);
    const erfuellung = prognoseKg && prognoseKg > 0 ? istKg / prognoseKg : null;

    const schlag = (m.fieldId && fieldNames[m.fieldId]) || 'Ohne Schlag';

    return {
      cropCycleId: m.cropCycleId,
      fieldId: m.fieldId,
      schlag,
      areaHa: m.areaHa,
      istKg,
      prognoseKg,
      planKg,
      erfuellung,
      letzterPfluecktag,
    };
  });
}

export type FieldSortKey =
  | 'schlag'
  | 'areaHa'
  | 'istKg'
  | 'prognoseKg'
  | 'planKg'
  | 'erfuellung'
  | 'letzterPfluecktag';

export type FieldSortDir = 'asc' | 'desc';

/** Numeric columns open desc (big first); Schlag + Pflücktag open asc. */
export const FIELD_NUMERIC_KEYS: ReadonlySet<FieldSortKey> = new Set<FieldSortKey>([
  'areaHa',
  'istKg',
  'prognoseKg',
  'planKg',
  'erfuellung',
]);

function sortValue(row: FieldRow, key: FieldSortKey): string | number | null {
  switch (key) {
    case 'schlag':
      return row.schlag;
    case 'areaHa':
      return row.areaHa;
    case 'istKg':
      return row.istKg;
    case 'prognoseKg':
      return row.prognoseKg;
    case 'planKg':
      return row.planKg;
    case 'erfuellung':
      return row.erfuellung;
    case 'letzterPfluecktag':
      return row.letzterPfluecktag;
  }
}

/**
 * Interactive column sort — missing values park LAST in BOTH directions (the
 * null test sits outside the direction flip); strings collate de. Non-mutating.
 */
export function sortFieldRows(rows: FieldRow[], key: FieldSortKey, dir: FieldSortDir): FieldRow[] {
  return [...rows].sort((a, b) => {
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    if (typeof av === 'string' && typeof bv === 'string') {
      const cmp = av.localeCompare(bv, 'de');
      return dir === 'asc' ? cmp : -cmp;
    }
    const cmp = (av as number) - (bv as number);
    return dir === 'asc' ? cmp : -cmp;
  });
}

/** The Σ ``<tfoot>`` row: summed columns; a column of all-null totals to null.
 *  Erfüllung is the aggregate Σ Ist / Σ Prognose (never a mean of the rows). */
export interface FieldRowsTotal {
  areaHa: number | null;
  istKg: number;
  prognoseKg: number | null;
  planKg: number | null;
  erfuellung: number | null;
}

export function fieldRowsTotal(rows: FieldRow[]): FieldRowsTotal {
  const sumOrNull = (pick: (r: FieldRow) => number | null): number | null => {
    const present = rows.map(pick).filter((v): v is number => v != null);
    return present.length > 0 ? round1(present.reduce((a, b) => a + b, 0)) : null;
  };
  const istKg = round1(rows.reduce((acc, r) => acc + r.istKg, 0));
  const prognoseKg = sumOrNull((r) => r.prognoseKg);
  return {
    areaHa: sumOrNull((r) => r.areaHa),
    istKg,
    prognoseKg,
    planKg: sumOrNull((r) => r.planKg),
    erfuellung: prognoseKg && prognoseKg > 0 ? istKg / prognoseKg : null,
  };
}

// ── #2459 field-select (Gesamt default) ──────────────────────────────────────

/**
 * Resolve the ``?f=`` field-select param (#2459). A Schlag selection holds ONLY
 * while its crop_cycle is still a current member — absent or stale (the field
 * left the current member set after a crop/variety change or a data refresh) ⇒
 * ``null`` = Gesamt, the aggregate default. Keeps the rendered==selectable
 * invariant: the table can never highlight a Schlag the chart cannot narrow to.
 */
export function resolveSelectedField(fParam: string | null, memberIds: readonly string[]): string | null {
  return fParam != null && memberIds.includes(fParam) ? fParam : null;
}

/**
 * Narrow any crop_cycle-keyed series (picks / forecasts / plans) to one selected
 * Schlag's cycle (#2459); a ``null`` selection ⇒ the whole set unchanged (Gesamt
 * aggregate). Non-mutating — the array identity only changes when narrowing.
 */
export function narrowToCycle<T extends { cropCycleId: string | null }>(
  items: T[],
  cropCycleId: string | null,
): T[] {
  return cropCycleId == null ? items : items.filter((i) => i.cropCycleId === cropCycleId);
}

/**
 * Narrow a member-cycle list to ONE Schlag by ``field_id`` (#2505) — the join
 * key for prior-year scoping: a Schlag's crop_cycle id differs every crop_year
 * (``?f=`` holds the CURRENT WJ's id), so ``field_id`` is the only stable key
 * across years. A ``null`` fieldId ⇒ EMPTY, never a ``null === null`` match —
 * an "Ohne Schlag" selection must not silently merge other unassigned cycles'
 * prior-year data under a shared null key (no curve beats a wrong curve).
 * Non-mutating.
 */
export function narrowMembersToField(
  members: OutputCycleInfo[],
  fieldId: string | null,
): OutputCycleInfo[] {
  return fieldId == null ? [] : members.filter((m) => m.fieldId === fieldId);
}
